if (!customElements.get('quick-add-modal')) {
  customElements.define(
    'quick-add-modal',
    class QuickAddModal extends ModalDialog {
      constructor() {
        super();
        this.modalContent = this.querySelector('[id^="QuickAddInfo-"]');
      }

      hide(preventFocus = false) {
        const cartNotification = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        if (cartNotification) cartNotification.setActiveElement(this.openedBy);
        
        // Destroy sliders before clearing content
        this.destroySliders();
        this.modalContent.innerHTML = '';

        if (preventFocus) this.openedBy = null;
        super.hide();
      }

      show(opener) {
        opener.setAttribute('aria-disabled', true);
        opener.classList.add('loading');
        opener.querySelector('.loading__spinner').classList.remove('hidden');

        fetch(opener.getAttribute('data-product-url'))
          .then((response) => response.text())
          .then((responseText) => {
            const responseHTML = new DOMParser().parseFromString(responseText, 'text/html');
            this.productElement = responseHTML.querySelector('section[id^="MainProduct-"]');
            this.productElement.classList.forEach((classApplied) => {
              if (classApplied.startsWith('color-') || classApplied === 'gradient')
                this.modalContent.classList.add(classApplied);
            });
            this.preventDuplicatedIDs();
            this.removeDOMElements();
            this.setInnerHTML(this.modalContent, this.productElement.innerHTML);

            if (window.Shopify && Shopify.PaymentButton) {
              Shopify.PaymentButton.init();
            }

            if (window.ProductModel) window.ProductModel.loadShopifyXR();

            this.removeGalleryListSemantic();
            this.updateImageSizes();
            this.preventVariantURLSwitching();
            super.show(opener);
            // Initialize sliders after modal is shown and rendered
            // Use multiple requestAnimationFrame calls to ensure modal is fully rendered
            requestAnimationFrame(() => {
              requestAnimationFrame(() => {
                setTimeout(() => {
                  this.initializeSlickSliders();
                  this.initializeSwiper();
                }, 300);
              });
            });
          })
          .finally(() => {
            opener.removeAttribute('aria-disabled');
            opener.classList.remove('loading');
            opener.querySelector('.loading__spinner').classList.add('hidden');
          });
      }

      setInnerHTML(element, html) {
        element.innerHTML = html;

        // Reinjects the script tags to allow execution. By default, scripts are disabled when using element.innerHTML.
        element.querySelectorAll('script').forEach((oldScriptTag) => {
          const scriptContent = oldScriptTag.innerHTML;
          const scriptSrc = oldScriptTag.getAttribute('src');
          
          // Handle external script tags (like slick.js)
          if (scriptSrc) {
            // Check if script is already loaded
            const existingScript = document.querySelector(`script[src="${scriptSrc}"]`);
            if (!existingScript) {
              const newScriptTag = document.createElement('script');
              Array.from(oldScriptTag.attributes).forEach((attribute) => {
                newScriptTag.setAttribute(attribute.name, attribute.value);
              });
              document.head.appendChild(newScriptTag);
            }
            oldScriptTag.remove();
            return;
          }
          
          // Skip scripts that are for slick/swiper initialization - we handle those separately
          if (scriptContent.includes('slick') || scriptContent.includes('Swiper') || scriptContent.includes('DOMContentLoaded')) {
            // These will be handled by our initialization methods
            return;
          }
          
          const newScriptTag = document.createElement('script');
          Array.from(oldScriptTag.attributes).forEach((attribute) => {
            newScriptTag.setAttribute(attribute.name, attribute.value);
          });
          
          // Wrap script content in try-catch and add null checks for addEventListener
          const wrappedScript = `
            (function() {
              try {
                ${scriptContent}
              } catch (e) {
                console.warn('Script error in quick view modal:', e);
              }
            })();
          `;
          
          newScriptTag.appendChild(document.createTextNode(wrappedScript));
          oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
        });
      }

      preventVariantURLSwitching() {
        const variantPicker = this.modalContent.querySelector('variant-selects');
        if (!variantPicker) return;

        variantPicker.setAttribute('data-update-url', 'false');
      }

      removeDOMElements() {
        const pickupAvailability = this.productElement.querySelector('pickup-availability');
        if (pickupAvailability) pickupAvailability.remove();

        const productModal = this.productElement.querySelector('product-modal');
        if (productModal) productModal.remove();

        const modalDialog = this.productElement.querySelectorAll('modal-dialog');
        if (modalDialog) modalDialog.forEach((modal) => modal.remove());
      }

      preventDuplicatedIDs() {
        const sectionId = this.productElement.dataset.section;
        this.productElement.innerHTML = this.productElement.innerHTML.replaceAll(sectionId, `quickadd-${sectionId}`);
        this.productElement.querySelectorAll('variant-selects, product-info').forEach((element) => {
          element.dataset.originalSection = sectionId;
        });
      }

      removeGalleryListSemantic() {
        const galleryList = this.modalContent.querySelector('[id^="Slider-Gallery"]');
        if (!galleryList) return;

        galleryList.setAttribute('role', 'presentation');
        galleryList.querySelectorAll('[id^="Slide-"]').forEach((li) => li.setAttribute('role', 'presentation'));
      }

      updateImageSizes() {
        const product = this.modalContent.querySelector('.product');
        const desktopColumns = product.classList.contains('product--columns');
        if (!desktopColumns) return;

        const mediaImages = product.querySelectorAll('.product__media img');
        if (!mediaImages.length) return;

        let mediaImageSizes =
          '(min-width: 1000px) 715px, (min-width: 750px) calc((100vw - 11.5rem) / 2), calc(100vw - 4rem)';

        if (product.classList.contains('product--medium')) {
          mediaImageSizes = mediaImageSizes.replace('715px', '605px');
        } else if (product.classList.contains('product--small')) {
          mediaImageSizes = mediaImageSizes.replace('715px', '495px');
        }

        mediaImages.forEach((img) => img.setAttribute('sizes', mediaImageSizes));
      }

      initializeSlickSliders() {
        // Ensure slick is loaded - load it if not available
        const loadSlickIfNeeded = (callback) => {
          if (typeof jQuery !== 'undefined' && typeof jQuery.fn.slick === 'function') {
            callback();
            return;
          }
          
          // Check if slick script is already in the page
          const existingSlickScript = document.querySelector('script[src*="slick"]');
          if (existingSlickScript) {
            // Script exists, wait for it to load
            let attempts = 0;
            const checkSlick = () => {
              attempts++;
              if (typeof jQuery !== 'undefined' && typeof jQuery.fn.slick === 'function') {
                callback();
              } else if (attempts < 50) {
                setTimeout(checkSlick, 100);
              }
            };
            setTimeout(checkSlick, 100);
            return;
          }
          
          // Load jQuery if not available
          if (typeof jQuery === 'undefined') {
            const jqueryScript = document.createElement('script');
            jqueryScript.src = 'https://code.jquery.com/jquery-3.6.0.min.js';
            jqueryScript.onload = () => {
              // After jQuery loads, load slick
              loadSlick();
            };
            document.head.appendChild(jqueryScript);
          } else {
            loadSlick();
          }
          
          function loadSlick() {
            // Load slick CSS
            if (!document.querySelector('link[href*="slick.min.css"]')) {
              const slickCSS = document.createElement('link');
              slickCSS.rel = 'stylesheet';
              slickCSS.href = 'https://cdnjs.cloudflare.com/ajax/libs/slick-carousel/1.8.1/slick.min.css';
              document.head.appendChild(slickCSS);
            }
            
            if (!document.querySelector('link[href*="slick-theme.min.css"]')) {
              const slickThemeCSS = document.createElement('link');
              slickThemeCSS.rel = 'stylesheet';
              slickThemeCSS.href = 'https://cdnjs.cloudflare.com/ajax/libs/slick-carousel/1.8.1/slick-theme.min.css';
              document.head.appendChild(slickThemeCSS);
            }
            
            // Load slick JS
            const slickScript = document.createElement('script');
            slickScript.src = 'https://cdnjs.cloudflare.com/ajax/libs/slick-carousel/1.8.1/slick.min.js';
            slickScript.onload = () => {
              // Wait a bit for slick to initialize
              setTimeout(callback, 100);
            };
            document.head.appendChild(slickScript);
          }
        };
        
        // Wait for jQuery and slick to be available, and for DOM to be ready
        let attempts = 0;
        const maxAttempts = 50; // Try for up to 5 seconds (50 * 100ms)
        
        const initSliders = () => {
          attempts++;
          
          if (typeof jQuery === 'undefined' || typeof jQuery.fn.slick === 'undefined') {
            if (attempts === 1) {
              // First attempt - try to load slick
              loadSlickIfNeeded(() => {
                setTimeout(initSliders, 200);
              });
            } else if (attempts < maxAttempts) {
              setTimeout(initSliders, 100);
            }
            return;
          }

          const $modalContent = jQuery(this.modalContent);
          
          // Check if slick sliders exist in the modal content
          const $sliderFor = $modalContent.find('.slider-for');
          const $sliderNav = $modalContent.find('.slider-nav');
          const $mobileSlider = $modalContent.find('.mobile_product_slider');

          // Initialize main slider if it exists and isn't already initialized
          if ($sliderFor.length > 0 && !$sliderFor.hasClass('slick-initialized')) {
            try {
              $sliderFor.slick({
                slidesToShow: 1,
                slidesToScroll: 1,
                arrows: true,
                fade: true,
                asNavFor: '.slider-nav'
              });
            } catch (e) {
              console.error('Error initializing slider-for:', e);
            }
          }

          // Initialize navigation slider if it exists and isn't already initialized
          if ($sliderNav.length > 0 && !$sliderNav.hasClass('slick-initialized')) {
            try {
              $sliderNav.slick({
                slidesToShow: 4,
                slidesToScroll: 1,
                asNavFor: '.slider-for',
                dots: false,
                centerMode: false,
                focusOnSelect: true,
                vertical: true
              });
            } catch (e) {
              console.error('Error initializing slider-nav:', e);
            }
          }

          // Initialize mobile slider if it exists and isn't already initialized
          // Initialize regardless of screen size - CSS will handle visibility
          if ($mobileSlider.length > 0 && !$mobileSlider.hasClass('slick-initialized')) {
            // Make sure the slider has children before initializing
            const sliderChildren = $mobileSlider.children();
            if (sliderChildren.length > 0) {
              try {
                // Get parent container and ensure visibility
                const $mobileSliderContainer = $mobileSlider.closest('.mobile_slider');
                
                // Force visibility for initialization (CSS will handle display on mobile/desktop)
                $mobileSlider.css({ 
                  display: 'block', 
                  visibility: 'visible', 
                  opacity: '1'
                });
                
                if ($mobileSliderContainer.length) {
                  $mobileSliderContainer.css({ 
                    display: 'block', 
                    visibility: 'visible'
                  });
                }
                
                // Use exact same initialization code as product page
                // Wait for jQuery and slick to be available, then initialize
                const initMobileSlider = (retryCount = 0) => {
                  const maxRetries = 20;
                  
                  // Check if jQuery and slick are available
                  if (typeof jQuery === 'undefined' || typeof jQuery.fn === 'undefined' || typeof jQuery.fn.slick === 'undefined') {
                    // jQuery or slick not loaded yet, retry
                    if (retryCount < maxRetries) {
                      setTimeout(() => initMobileSlider(retryCount + 1), 100);
                    } else {
                      console.error('jQuery or Slick not available after retries');
                    }
                    return;
                  }
                  
                  // Re-query to ensure we have fresh references
                  const $slider = $modalContent.find('.mobile_product_slider');
                  if ($slider.length === 0) {
                    // Slider not found yet, retry
                    if (retryCount < maxRetries) {
                      setTimeout(() => initMobileSlider(retryCount + 1), 100);
                    }
                    return;
                  }
                  
                  if ($slider.hasClass('slick-initialized')) {
                    return; // Already initialized
                  }
                  
                  // Verify slick is a function on the jQuery object
                  if (typeof jQuery.fn.slick !== 'function') {
                    console.error('jQuery.fn.slick is not a function');
                    if (retryCount < maxRetries) {
                      setTimeout(() => initMobileSlider(retryCount + 1), 200);
                    }
                    return;
                  }
                  
                  // Exact match to product page script - $(document).ready equivalent
                  const $progressBar = $modalContent.find('.progress');
                  const $progressBarLabel = $modalContent.find('.slider__label');
                  const $slideCount = $modalContent.find('.slide-count');

                  // Set up event handlers exactly as product page
                  $slider.on('init reInit afterChange', function(event, slick, currentSlide, nextSlide){
                    // Current slide is zero-based
                    const i = (currentSlide ? currentSlide : 0) + 1;
                    if ($slideCount.length) {
                      $slideCount.text(i + '/' + slick.slideCount);
                    }
                  });

                  $slider.on('beforeChange', function(event, slick, currentSlide, nextSlide) {
                    const calc = ((nextSlide) / (slick.slideCount - 1)) * 100;
                    if ($progressBar.length) {
                      $progressBar
                        .css('background-size', calc + '% 100%')
                        .attr('aria-valuenow', calc);
                    }
                    if ($progressBarLabel.length) {
                      $progressBarLabel.text(calc.toFixed(0) + '% completed');
                    }
                  });

                  // Initialize slick - exact same config as product page
                  try {
                    $slider.slick({
                      slidesToShow: 1,
                      slidesToScroll: 1,
                      arrows: false,
                      speed: 400
                    });
                    
                    // Remove video controls - exact same as product page
                    $modalContent.find('.custom-thumb-video-slide-mb').removeAttr('controls');
                    
                    // Ensure slick recalculates after initialization
                    setTimeout(() => {
                      if ($slider.hasClass('slick-initialized')) {
                        $slider.slick('setPosition');
                        // Trigger resize to ensure proper sizing
                        if (window.dispatchEvent) {
                          window.dispatchEvent(new Event('resize'));
                        }
                        if (typeof jQuery !== 'undefined') {
                          jQuery(window).trigger('resize');
                        }
                      }
                    }, 300);
                  } catch (e) {
                    console.error('Error calling slick():', e);
                    if (retryCount < maxRetries) {
                      setTimeout(() => initMobileSlider(retryCount + 1), 200);
                    }
                  }
                };
                
                // Start initialization after a delay to ensure DOM is ready
                setTimeout(() => initMobileSlider(0), 200);
              } catch (e) {
                console.error('Error initializing mobile slider:', e);
                // Retry if it failed and we haven't exceeded max attempts
                if (attempts < maxAttempts) {
                  setTimeout(initSliders, 300);
                }
              }
            } else if (attempts < maxAttempts) {
              // Slider exists but no children yet, retry
              setTimeout(initSliders, 100);
              return;
            }
          }

          // Remove video controls
          $modalContent.find('.slider-for video, .slider-nav video').removeAttr('controls');
          $modalContent.find('.custom-thumb-video-slide-mb').removeAttr('controls');

          // Add event listeners to remove video controls before slide change
          if ($sliderFor.length > 0) {
            $sliderFor.on('beforeChange', function(event, slick, currentSlide, nextSlide) {
              $modalContent.find('.slider-for video, .slider-nav video').removeAttr('controls');
            });
          }

          if ($sliderNav.length > 0) {
            $sliderNav.on('beforeChange', function(event, slick, currentSlide, nextSlide) {
              $modalContent.find('.slider-for video, .slider-nav video').removeAttr('controls');
            });
          }
        };

        // Match product page timing - use setTimeout like product page does
        setTimeout(initSliders, 100);
      }

      initializeSwiper() {
        // Wait for Swiper to be available - match product page initialization
        const initSwiper = () => {
          if (typeof Swiper === 'undefined') {
            setTimeout(initSwiper, 100);
            return;
          }

          const modalContent = this.modalContent;
          
          // Check if Swiper galleries exist in the modal content
          const thumbnailSwiperEl = modalContent.querySelector('.thumbnail-swiper');
          const mainSwiperEl = modalContent.querySelector('.main-swiper');

          if (thumbnailSwiperEl && mainSwiperEl) {
            // Check if already initialized
            if (thumbnailSwiperEl.swiper || mainSwiperEl.swiper) {
              return; // Already initialized
            }

            const slidesPerView = 5;
            const gap = 10;

            // Set thumbnail height - exact same as product page
            const setThumbnailHeight = () => {
              if (window.innerWidth >= 768) {
                const thumbSlide = modalContent.querySelector('.thumbnail-swiper .swiper-slide');
                if (thumbSlide) {
                  const thumbHeight = thumbSlide.offsetWidth; // aspect ratio is 1/1
                  const totalHeight = (thumbHeight + gap) * slidesPerView - gap;
                  thumbnailSwiperEl.style.height = `${totalHeight}px`;
                }
              } else {
                // Reset for mobile
                thumbnailSwiperEl.style.height = 'auto';
              }
            };

            // Set height on load and resize - exact same as product page
            setThumbnailHeight();
            window.addEventListener('resize', setThumbnailHeight);

            // Initialize Swipers after height is set - exact same config as product page
            const thumbnailSwiper = new Swiper(thumbnailSwiperEl, {
              direction: window.innerWidth >= 768 ? 'vertical' : 'horizontal',
              slidesPerView: slidesPerView,
              spaceBetween: gap,
              watchSlidesProgress: true,
              watchSlidesVisibility: true,
              freeMode: true,
              loop: false,
              breakpoints: {
                0: {
                  direction: 'horizontal'
                },
                768: {
                  direction: 'vertical'
                }
              }
            });

            const mainSwiper = new Swiper(mainSwiperEl, {
              spaceBetween: 10,
              loop: true,
              thumbs: {
                swiper: thumbnailSwiper
              }
            });

            // Sync swipers - exact same as product page
            mainSwiper.on('slideChange', () => {
              const realIndex = mainSwiper.realIndex;
              thumbnailSwiper.slideTo(realIndex);
            });

            // Navigation arrows - exact same as product page
            const thumbPrev = modalContent.querySelector('.thumb-prev');
            const thumbNext = modalContent.querySelector('.thumb-next');
            
            if (thumbPrev) {
              thumbPrev.addEventListener('click', () => {
                mainSwiper.slidePrev();
              });
            }

            if (thumbNext) {
              thumbNext.addEventListener('click', () => {
                mainSwiper.slideNext();
              });
            }
          }
        };

        // Use DOMContentLoaded equivalent timing - match product page
        setTimeout(initSwiper, 100);
      }

      destroySliders() {
        const modalContent = this.modalContent;
        
        // Destroy Slick sliders
        if (typeof jQuery !== 'undefined' && typeof jQuery.fn.slick !== 'undefined') {
          const $modalContent = jQuery(modalContent);
          $modalContent.find('.slider-for, .slider-nav, .mobile_product_slider').each(function() {
            if (jQuery(this).hasClass('slick-initialized')) {
              jQuery(this).slick('unslick');
            }
          });
        }

        // Destroy Swiper instances
        if (typeof Swiper !== 'undefined') {
          const thumbnailSwiperEl = modalContent.querySelector('.thumbnail-swiper');
          const mainSwiperEl = modalContent.querySelector('.main-swiper');
          
          if (thumbnailSwiperEl && thumbnailSwiperEl.swiper) {
            thumbnailSwiperEl.swiper.destroy(true, true);
          }
          
          if (mainSwiperEl && mainSwiperEl.swiper) {
            mainSwiperEl.swiper.destroy(true, true);
          }
        }
      }
    }
  );
}
